import axios from 'axios';

const API_URL = 'https://gymprojectbackend-production.up.railway.app/api';

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 60000,
});

// Request Interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        if (config.headers['Content-Type'] === 'multipart/form-data') {
            console.log('🚀 Upload Request:', config.method?.toUpperCase(), config.url);
        } else {
            console.log('🚀 API Request:', config.method?.toUpperCase(), config.url, config.data || '');
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor
api.interceptors.response.use(
    (response) => {
        console.log('✅ API Response:', response.status, response.config.url);
        return response;
    },
    (error) => {
        console.error('❌ API Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            data: error.response?.data
        });

        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }

        return Promise.reject(error);
    }
);

// Helper function
const apiCall = async (url, method = 'GET', data = null) => {
    try {
        const config = { method, url };
        if (data) config.data = data;

        const response = await api(config);
        return response.data;
    } catch (error) {
        console.error(`❌ API Error [${method} ${url}]: `, error.response?.data || error.message);
        throw error;
    }
};

// ===== AUTH API =====
export const authAPI = {
    login: async (email, password) => {
        const res = await api.post('/auth/login', { email, password });
        return res.data;
    },
    register: async (userData) => {
        const res = await api.post('/auth/register', userData);
        return res.data;
    }
};

// ===== DAYS API =====
export const dayAPI = {
    getAll: async () => {
        const res = await api.get('/days');
        return res.data || [];
    },
    getById: async (id) => {
        const res = await api.get(`/days/${id}`);
        return res.data;
    },
    getVideos: async (dayId) => {
        const res = await api.get(`/days/${dayId}/videos`);
        return res.data || [];
    },
    create: async (dayData) => {
        const res = await api.post('/days', dayData);
        return res.data;
    },
    update: async (id, dayData) => {
        const res = await api.put(`/days/${id}`, dayData);
        return res.data;
    },
    delete: async (id) => {
        await api.delete(`/admin/days/${id}`);
    },
    reorder: async (dayIds) => {
        const res = await api.put('/days/reorder', dayIds);
        return res.data;
    },

    admin: {
        getAll: async () => {
            const res = await api.get('/admin/days');
            return res.data || [];
        },
        getById: async (id) => {
            const res = await api.get(`/admin/days/${id}`);
            return res.data;
        },
        getVideos: async (dayId) => {
            const res = await api.get(`/days/${dayId}/videos`);
            return res.data || [];
        },
        create: async (dayData) => {
            const res = await api.post('/admin/days', dayData);
            return res.data;
        },
        update: async (id, dayData) => {
            const res = await api.put(`/admin/days/${id}`, dayData);
            return res.data;
        },
        delete: async (id) => {
            await api.delete(`/admin/days/${id}`);
        },
        reorder: async (dayIds) => {
            const res = await api.put('/admin/days/reorder', dayIds);
            return res.data;
        }
    }
};

// ===== VIDEOS API =====
export const videoAPI = {
    getAll: async () => {
        const res = await api.get('/videos');
        return res.data || [];
    },
    getByDay: async (dayId) => {
        try {
            const res = await api.get(`/videos/day/${dayId}`);
            console.log('🎬 API Response for day', dayId, ':', res.data);
            return res.data || [];
        } catch (error) {
            console.error('❌ API Error for day', dayId, ':', error);
            if (error.response?.status === 404) {
                return [];
            }
            throw error;
        }
    },
    getById: async (id) => {
        const res = await api.get(`/videos/${id}`);
        return res.data;
    },
    getVideoFile: async (videoId) => {
        const res = await api.get(`/videos/file/${videoId}`, {
            responseType: 'blob'
        });
        return res.data;
    },

    admin: {
        getAll: async () => {
            const res = await api.get('/admin/videos');
            return res.data || [];
        },
        getByDay: async (dayId) => {
            try {
                const res = await api.get(`/videos/day/${dayId}`);
                console.log('🎬 Admin API Response for day', dayId, ':', res.data);
                return res.data || [];
            } catch (error) {
                console.error('❌ Admin API Error for day', dayId, ':', error);
                if (error.response?.status === 404) {
                    return [];
                }
                throw error;
            }
        },
        getById: async (id) => {
            const res = await api.get(`/admin/videos/${id}`);
            return res.data;
        },
        create: async (videoData) => {
            const res = await api.post('/admin/videos', videoData);
            return res.data;
        },
        update: async (id, videoData) => {
            const res = await api.put(`/admin/videos/${id}`, videoData);
            return res.data;
        },
        // ✅ ПОПРАВЕНО - користи /admin/videos/{id}
        delete: async (id) => {
            await api.delete(`/admin/videos/${id}`);
        },
        reorder: async (videoIds) => {
            const res = await api.put('/videos/reorder', videoIds);
            return res.data;
        },
        updateOrder: async (id, newOrder) => {
            const res = await api.put(`/videos/${id}/order/${newOrder}`);
            return res.data;
        },
        upload: async (file, title, description = '', orderIndex = 1, dayId = null, onProgress = null) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', title);
            formData.append('description', description);
            formData.append('orderIndex', orderIndex.toString());
            if (dayId) formData.append('dayId', dayId.toString());

            console.log('📤 Uploading video:', {
                title, description, orderIndex, dayId,
                fileName: file.name,
                fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB'
            });

            const config = {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(percentCompleted);
                    }
                }
            };

            const res = await api.post('/videos/upload', formData, config);
            return res.data;
        },
        getFile: async (videoId) => {
            const res = await api.get(`/videos/file/${videoId}`, { responseType: 'blob' });
            return res.data;
        },
        uploadThumbnail: async (videoId, thumbnailFile) => {
            const formData = new FormData();
            formData.append('thumbnail', thumbnailFile);
            const config = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 };
            const res = await api.post(`/videos/${videoId}/thumbnail`, formData, config);
            return res.data;
        },
        deleteThumbnail: async (videoId) => {
            const res = await api.delete(`/videos/${videoId}/thumbnail`);
            return res.data;
        }
    }
};

// ===== USERS API =====
export const userAPI = {
    getAll: async () => {
        const res = await api.get('/users');
        return res.data || [];
    },
    getById: async (id) => {
        const res = await api.get(`/users/${id}`);
        return res.data;
    },
    admin: {
        getAll: async () => {
            const res = await api.get('/admin/users');
            return res.data || [];
        },
        getById: async (id) => {
            const res = await api.get(`/admin/users/${id}`);
            return res.data;
        },
        update: async (id, userData) => {
            const res = await api.put(`/admin/users/${id}`, userData);
            return res.data;
        },
        delete: async (id) => {
            await api.delete(`/admin/users/${id}`);
        },
        updateRole: async (id, role) => {
            const res = await api.put(`/admin/users/${id}/role?role=${role}`);
            return res.data;
        }
    }
};

// ===== PROGRAMS API =====
export const programAPI = {
    getAll: async () => {
        const res = await api.get('/programs');
        return res.data || [];
    },
    getById: async (id) => {
        const res = await api.get(`/programs/${id}`);
        return res.data;
    },
    getDetails: async (id) => {
        const res = await api.get(`/programs/${id}/details`);
        return res.data;
    },
    purchase: async (id) => {
        const res = await api.post(`/programs/${id}/purchase`);
        return res.data;
    },
    getMyPrograms: async () => {
        const res = await api.get('/programs/my-programs');
        return res.data || [];
    },
    getRemainingDays: async (id) => {
        const res = await api.get(`/programs/${id}/remaining-days`);
        return res.data;
    },
    decreaseRemainingDays: async (id) => {
        const res = await api.post(`/programs/${id}/decrease-days`);
        return res.data;
    },
    search: async (name) => {
        const res = await api.get(`/programs/search?name=${encodeURIComponent(name)}`);
        return res.data || [];
    },
    admin: {
        getAll: async () => {
            const res = await api.get('/admin/programs');
            return res.data || [];
        },
        getById: async (id) => {
            const res = await api.get(`/admin/programs/${id}`);
            return res.data;
        },
        create: async (programData) => {
            const res = await api.post('/admin/programs', programData);
            return res.data;
        },
        update: async (id, programData) => {
            const res = await api.put(`/admin/programs/${id}`, programData);
            return res.data;
        },
        delete: async (id) => {
            await api.delete(`/admin/programs/${id}`);
        },
        addDay: async (programId, dayId) => {
            const res = await api.post(`/admin/programs/${programId}/days/${dayId}`);
            return res.data;
        },
        removeDay: async (programId, dayId) => {
            await api.delete(`/admin/programs/${programId}/days/${dayId}`);
        },
        search: async (name) => {
            const res = await api.get(`/admin/programs/search?name=${encodeURIComponent(name)}`);
            return res.data || [];
        }
    }
};

// ===== GYM TRAINING API =====
export const gymTrainingAPI = {
    admin: {
        getAll: () => apiCall('/admin/trainings'),
        create: (data) => apiCall('/admin/trainings', 'POST', data),
        update: (id, data) => apiCall(`/admin/trainings/${id}`, 'PUT', data),
        delete: (id) => apiCall(`/admin/trainings/${id}`, 'DELETE'),
        getById: (id) => apiCall(`/admin/trainings/${id}`),
        search: (name) => apiCall(`/admin/trainings/search?name=${encodeURIComponent(name)}`),
        reorder: (trainingIds) => apiCall('/admin/trainings/reorder', 'PUT', trainingIds),
    },
    user: {
        getAll: () => apiCall('/trainings'),
        getById: (id) => apiCall(`/trainings/${id}`),
        purchase: (id) => apiCall(`/trainings/${id}/purchase`, 'POST'),
        getMyTrainings: () => apiCall('/trainings/my-trainings'),
        decreaseDays: (id) => apiCall(`/trainings/${id}/decrease-days`, 'POST'),
        getRemainingDays: (id) => apiCall(`/trainings/${id}/remaining-days`),
    }
};

// ===== DIETS API =====
export const dietAPI = {
    getAll: async () => {
        try {
            const res = await api.get('/diets');
            return res.data || [];
        } catch (error) {
            console.warn('⚠️ Error fetching diets:', error);
            return [];
        }
    },
    getById: async (id) => {
        const res = await api.get(`/diets/${id}`);
        return res.data;
    },
    purchase: async (id, userData) => {
        const res = await api.post(`/diets/${id}/purchase`, userData);
        return res.data;
    },
    getMyDiets: async () => {
        try {
            const res = await api.get('/diets/my-diets');
            return res.data || [];
        } catch (error) {
            console.error('⚠️ Error fetching my diets:', error);
            return [];
        }
    },
    search: async (name) => {
        const res = await api.get(`/diets/search?name=${encodeURIComponent(name)}`);
        return res.data || [];
    },
    admin: {
        getAll: () => apiCall('/admin/diets'),
        create: (data) => apiCall('/admin/diets', 'POST', data),
        update: (id, data) => apiCall(`/admin/diets/${id}`, 'PUT', data),
        delete: (id) => apiCall(`/admin/diets/${id}`, 'DELETE'),
        getById: (id) => apiCall(`/admin/diets/${id}`),
        search: (name) => apiCall(`/admin/diets/search?name=${encodeURIComponent(name)}`),
    }
};

// ===== ANALYTICS API =====
export const analyticsAPI = {
    admin: {
        getDashboardStats: () => apiCall('/admin/analytics/dashboard'),
        getUserStats: () => apiCall('/admin/analytics/users'),
        getProgramStats: () => apiCall('/admin/analytics/programs'),
        getRevenueStats: () => apiCall('/admin/analytics/revenue'),
        getTrainingStats: () => apiCall('/admin/analytics/trainings'),
        getDietStats: () => apiCall('/admin/analytics/diets'),
    }
};

// ===== GENERAL STATS API =====
export const statsAPI = {
    admin: {
        getCounts: async () => {
            try {
                const [programs, users, trainings, diets] = await Promise.allSettled([
                    programAPI.admin.getAll(),
                    userAPI.admin.getAll(),
                    gymTrainingAPI.admin.getAll(),
                    dietAPI.admin.getAll()
                ]);

                return {
                    totalPrograms: programs.status === 'fulfilled' ? programs.value.length : 0,
                    totalUsers: users.status === 'fulfilled' ? users.value.length : 0,
                    totalTrainings: trainings.status === 'fulfilled' ? trainings.value.length : 0,
                    totalDiets: diets.status === 'fulfilled' ? diets.value.length : 0,
                };
            } catch (error) {
                console.error('Error fetching stats:', error);
                return { totalPrograms: 0, totalUsers: 0, totalTrainings: 0, totalDiets: 0 };
            }
        }
    }
};

// ===== SUBSCRIPTION API =====
export const subscriptionAPI = {
    user: {
        getCurrent: () => apiCall('/subscriptions/current'),
        purchase: (subscriptionId) => apiCall(`/subscriptions/${subscriptionId}/purchase`, 'POST'),
        cancel: () => apiCall('/subscriptions/cancel', 'POST'),
        getHistory: () => apiCall('/subscriptions/history'),
    },
    admin: {
        getAll: () => apiCall('/admin/subscriptions'),
        create: (data) => apiCall('/admin/subscriptions', 'POST', data),
        update: (id, data) => apiCall(`/admin/subscriptions/${id}`, 'PUT', data),
        delete: (id) => apiCall(`/admin/subscriptions/${id}`, 'DELETE'),
    }
};

// ===== CONTACT INFO API =====
export const contactInfoAPI = {
    get: async () => {
        const res = await api.get('/public/contact-info');
        return res.data;
    },
    admin: {
        get: async () => {
            const res = await api.get('/admin/contact-info');
            return res.data;
        },
        update: async (data) => {
            const res = await api.put('/admin/contact-info', data);
            return res.data;
        }
    }
};

// ===== EXPORT DEFAULT API INSTANCE =====
export default api;